import { ReactElement, useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { Button, Box, Text, Flex } from "@chakra-ui/react";
import { useCountdownLogout } from "../../../../hooks/useCountdownLogout";
import {
  useAssertEngineTypeFromPathParam,
  useGetAnalysisEngines,
} from "../../../../utils/selectAnalysisEngine";
import { employeeInfoAtom, outputStenScoreAtom } from "../../../../store";
import {
  latestStenScoreAtom,
  latestVitalityStringAtom,
  latestVitalityAtom,
  latestMentalActivityAtom,
  latestMentalActivityStringAtom,
  isGetMimosysDataAtom,
  latestMi1ResultReliabilityAtom,
  latestMentalActivityReliabilityAtom,
} from "../../../../stores/mimosysStore";
import { useAtom } from "jotai";
import { LargeRadialChart } from "../../../../components/atoms/ResultCommon/RadialChart";
import {
  latestMentalActivityTrendAtom,
  latestVitalityTrendAtom,
} from "../../../../components/atoms/ResultHome/HomeAtoms";

import { APP_TYPE, KOKORO_IMAGE_URL } from "../../../../environments";
import {
  MentalActivityReferenceMarker,
  StenReferenceMarker,
} from "../../../../components/atoms/ResultCommon/StenReferenceMarker";
import { useModifiedTranslation } from "../../../../hooks/useModifiedTranslation";
import { PocResult } from "../../../../components/molecules/PocResult";
import { KokoroSvg } from "../../../../components/atoms/ResultCommon/KokoroSvg";
import { NumericDataText } from "../../../../components/atoms/NumericDataText";
import { BasicPageLayout } from "../../../../components/templates/BasicPageLayout";
import { useColorToken } from "../../../../hooks/useColorToken";

function MimosysChart(): ReactElement {
  const [latestVitality] = useAtom(latestVitalityAtom);
  const [latestVitalityString] = useAtom(latestVitalityStringAtom);
  const [latestVitalityTrend] = useAtom(latestVitalityTrendAtom);
  const [latestMentalActivity] = useAtom(latestMentalActivityAtom);
  const [latestMentalActivityString] = useAtom(latestMentalActivityStringAtom);
  const [latestMentalActivityTrend] = useAtom(latestMentalActivityTrendAtom);
  const [latestMentalActivityReliability] = useAtom(
    latestMentalActivityReliabilityAtom,
  );
  const mentalActivityReliability = latestMentalActivityReliability !== false;

  const t = useModifiedTranslation();

  const vitalityColor = useColorToken("vitality.primary");
  const mentalActivityColor = useColorToken("mentalActivity.primary");

  return (
    <>
      <Flex p="6" w="full" maxW="100%">
        <Box
          bg="common.base"
          boxShadow="md"
          rounded="xl"
          paddingTop="3"
          width="100%"
        >
          {/* 元気圧 */}
          <LargeRadialChart
            progress={latestVitality}
            barColor={vitalityColor}
            score={latestVitalityString}
            title={t("Graph.common.vitality")}
            trend={latestVitalityTrend}
          />
        </Box>
      </Flex>
      <Box p="6">
        {mentalActivityReliability || (
          <Box>
            <MentalActivityReferenceMarker />
          </Box>
        )}
        <Flex w="full" maxW="100%">
          <Box
            bg="common.base"
            boxShadow="md"
            rounded="xl"
            paddingTop="3"
            width="100%"
          >
            {/* 心の活量値 */}
            <LargeRadialChart
              progress={latestMentalActivity}
              barColor={mentalActivityColor}
              score={latestMentalActivityString}
              title={t("Graph.common.mentalActivity")}
              trend={latestMentalActivityTrend}
            />
          </Box>
        </Flex>
      </Box>
    </>
  );
}

function Mi1Chart(): ReactElement {
  const [latestStenScore] = useAtom(latestStenScoreAtom) as number[];
  const [latestMi1ResultReliability] = useAtom(latestMi1ResultReliabilityAtom);
  const stenReliability = latestMi1ResultReliability !== false;
  const t = useModifiedTranslation();

  return (
    <>
      {stenReliability || (
        <Box>
          <StenReferenceMarker />
        </Box>
      )}

      <KokoroSvg imageIndex={latestStenScore === null ? 0 : latestStenScore} />

      <Flex
        bg="common.base"
        w="full"
        maxW="100%"
        justifyContent="space-around"
        alignItems="end"
        p={5}
      >
        <Box>
          {/* こころ指数 */}
          <Text fontSize="clamp(2rem, 5vw, 3rem)">
            {t("Graph.common.mi1Result")}
          </Text>
          {/* 個人最適化した10段階評価 */}
          <Text fontSize="clamp(0.75rem, 4vw, 1.25rem)" fontWeight="normal">
            {t("Graph.common.mi1ResultExplanation")}
          </Text>
        </Box>
        <Box>
          {/* こころ指数 */}
          <Text textAlign="center" fontSize="clamp(3rem, 5vw, 4.5rem)">
            {latestStenScore}
          </Text>
          {/* 個人最適化した10段階評価 */}
          <Text
            textAlign="center"
            fontSize="clamp(0.75rem, 4vw, 1.25rem)"
            whiteSpace="nowrap"
          >
            [1-10]
          </Text>
        </Box>
      </Flex>
    </>
  );
}

export function Result(): ReactElement {
  const t = useModifiedTranslation();
  const engineType = useAssertEngineTypeFromPathParam();
  const analysisEngines = useGetAnalysisEngines(engineType);
  const [recordedAt, setRecordedAt] = useState("");
  const [engineResultElements, setEngineResultElements] = useState<
    ReactElement[] | null
  >(null);
  const { countdown, logout } = useCountdownLogout(3);

  const [recordedDate, recordedTime] = recordedAt.split(" ");
  const { fileId } = useParams();

  const [isGetMimosysData] = useAtom(isGetMimosysDataAtom);

  useEffect(() => {
    if (!fileId) return;

    // 元気圧・心の活量値・こころ指数のメーター表示用に最終結果を（「いい調子」「普段通り」「疲れ気味」の判定を含む）初期化
    const renderingPromises = analysisEngines.map((engine, engineIndex) =>
      engine.renderResult(engineIndex, fileId, t),
    );
    Promise.all(renderingPromises).then((renderingResults) => {
      setEngineResultElements(renderingResults);
    });
    const analysisResultData = analysisEngines.map((engine) =>
      engine.getLatestRecordedAt(fileId),
    );
    Promise.all(analysisResultData).then((result) => {
      const recorded_at = new Date(result[result.length - 1]);
      setRecordedAt(recorded_at.toLocaleString("ja"));
    });
  }, [fileId, t, analysisEngines]);

  const [outputStenScore] = useAtom(outputStenScoreAtom);

  // NOTE: こころ指数画像のプリロード、もっと良い方法があるかも
  // FIXME: 解析中画面は、結果画面に遷移してから表示すべき
  const [loaded, setLoaded] = useState(!outputStenScore);
  const [latestStenScore] = useAtom(latestStenScoreAtom);

  const kokoroImageIdx = latestStenScore === null ? 0 : latestStenScore;
  const kokoroImageUrl = outputStenScore
    ? KOKORO_IMAGE_URL[kokoroImageIdx]
    : null;

  useEffect(() => {
    if (kokoroImageUrl) {
      const img = new Image();
      img.src = kokoroImageUrl;
      img.onload = () => {
        setLoaded(true);
      };
    }
  }, [kokoroImageUrl]);

  const [employeeInfo] = useAtom(employeeInfoAtom);

  if (!fileId) {
    return <Navigate to="/404" replace />;
  } else if (engineResultElements !== null && isGetMimosysData) {
    return loaded ? (
      <BasicPageLayout
        title={employeeInfo.employee_name + t("Graph.common.personTitle")}
        minH="full"
        gap={2}
      >
        {APP_TYPE === "poc" && <PocResult />}
        {APP_TYPE !== "poc" && (
          <>
            <Flex justifyContent="center" alignItems="baseline">
              <NumericDataText fontSize="3xl">
                {recordedDate}&nbsp;
              </NumericDataText>
              <NumericDataText fontSize="2xl">{recordedTime}</NumericDataText>
            </Flex>

            {outputStenScore && <Mi1Chart />}
            {!outputStenScore && <MimosysChart />}
          </>
        )}
        <Button
          onClick={logout}
          size="lg"
          variant="btn_primary"
          width="100%"
          minHeight="64px"
          mt="auto"
        >
          {t("QueryLogin.common.resultTitle")} {countdown}
        </Button>
      </BasicPageLayout>
    ) : (
      <></>
    );
  } else {
    return <></>;
  }
}
